import React, { forwardRef } from "react";
import { bool, func, oneOf, string } from "prop-types";
import { uncontrollable } from "uncontrollable";

import BaseTextArea from "./styled";

export const TextArea = forwardRef(
  (
    {
      height,
      isDisabled,
      isInvalid,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      resize,
      value,
      ...passThroughProps
    },
    ref
  ) => {
    const handleEvent = event => {
      const { value: eventValue } = event.target;
      switch (event.type) {
        case "focus":
          onFocus(eventValue, event);
          break;
        case "blur":
          onBlur(eventValue, event);
          break;
        case "change":
          onChange(eventValue, event);
          break;
        default:
        // nothing happens
      }
    };

    return (
      <BaseTextArea
        ref={ref}
        height={height}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        onBlur={handleEvent}
        onChange={handleEvent}
        onFocus={handleEvent}
        placeholder={placeholder}
        resize={resize}
        value={value}
        {...passThroughProps}
      />
    );
  }
);

TextArea.displayName = "TextArea";

TextArea.propTypes = {
  /**
   * Textarea element height.
   */
  height: string,
  /**
   * Enables disable state styling.
   */
  isDisabled: bool,
  /**
   * Enables error state styling.
   */
  isInvalid: bool,
  /**
   * Callback for when TextArea loses focus.
   *
   * @param {string} value The textarea's value
   * @param {SyntheticEvent} event The react `SyntheticEvent`
   */
  onBlur: func,
  /**
   * Callback for when TextArea value changes.
   *
   * @param {string} value The textarea's value
   * @param {SyntheticEvent} event The react `SyntheticEvent`
   */
  onChange: func,
  /**
   * Callback for when TextArea gains focus.
   *
   * @param {string} value The textarea's value
   * @param {SyntheticEvent} event The react `SyntheticEvent`
   */
  onFocus: func,
  /**
   *  Placeholder for textarea element.
   */
  placeholder: string,
  /**
   * Textarea resize behavior.
   */
  resize: oneOf(["vertical", "horizontal", "none", "both"]),
  /**
   * Value for textarea element.
   */
  value: string
};

TextArea.defaultProps = {
  height: "200px",
  isDisabled: false,
  isInvalid: false,
  onBlur: () => {},
  onChange: () => {},
  onFocus: () => {},
  placeholder: "",
  resize: "vertical",
  value: ""
};

/**
 * @deprecated please use "@streeteasy/design-system/form/TextArea" instead
 */

export default uncontrollable(TextArea, {
  value: "onChange"
});
